import { FC, memo, useCallback, useState } from 'react';
import LocationModal from '@src/modals/LocationModal/LocationModal';
import AuthModal from '@src/modals/AuthModal/AuthModal';
import { LocationModalWrapperPropsType } from '@uikit/FormItems/constants';
import {
  selectLocations,
  setOpenAuthModal,
  setSupervisorApprove,
} from '@store/slices/locations/locationsSlice';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { FormProvider, useForm } from 'react-hook-form';
import { LOCATION_DEFAULT_VALUES } from '@src/modals/LocationModal/constants';
import { toast } from 'react-toastify';
import { useCreateOrUpdateLocationMutation } from '@store/rtk/global';
import { useSupervisorAccess } from '@hooks/useSupervisorAccess';
import { selectUsers } from '@store/slices/users/usersSlice';
import { DEFAULT_NOTE_CATEGORY } from '@src/utils';
import { useCreateOrUpdateNotesMutation } from '@src/store/rtk/notes';
import { selectGlobal } from '@src/store/slices/global/global';
import { toStateAbbreviation } from '@component/Locations/constants';
import moment from 'moment';
import { toastError } from '@utils/helpers';
import { toastMessage } from '@utils/toastMessage';

const LocationModalWrapper: FC<LocationModalWrapperPropsType> = ({
  children,
  isInModal,
  toggleLocationModal,
  setToggleLocationModal,
  change,
  setValueHasSaved,
  setSavedValue,
  index,
  setVerifiedAddress,
  searchInputValue = '',
  dataTestId,
}) => {
  const { supervisorPermissions } = useSupervisorAccess();
  const { openAuthModal, rideLocationData } = useAppSelector(selectLocations);
  const { userInfo } = useAppSelector(selectUsers);
  const { shouldSupervisorApprove } = useAppSelector(selectLocations);
  const { currentPath } = useAppSelector(selectGlobal);
  const [createOrUpdateNotes] = useCreateOrUpdateNotesMutation();
  const dispatch = useAppDispatch();

  const [toggleSupervisorOverride, setToggleSupervisorOverride] =
    useState<boolean>(false);
  const [createOrUpdateLocation, { isLoading }] =
    useCreateOrUpdateLocationMutation();
  const methods = useForm({
    defaultValues: LOCATION_DEFAULT_VALUES,
    mode: 'onSubmit',
  });

  const { getValues } = methods;
  const handleAuthTrigger = useCallback(() => {
    setValueHasSaved(true);
    if (!toggleLocationModal?.address) {
      dispatch(setOpenAuthModal(true));
    }
  }, [toggleLocationModal?.address]);

  const handleCloseAuthModal = () => {
    dispatch(setOpenAuthModal(false));
    dispatch(setSupervisorApprove(true));
  };

  const isEmptyStr = (str: string) => str || '';

  const handleSaveDataAfterNote = async (
    data: any,
    info?: { firstName: string; lastName: string; id: number }
  ) => {
    const newData = {
      ...data,
      type: getValues('type')?.id,
      state: getValues('state')?.value,
      country: getValues('country'),
      city: getValues('city'),
      zipCode: getValues('zipCode'),
    };

    const dynamicData = !data.address1
      ? {
          ...rideLocationData,
          type: rideLocationData?.type?.id,
          state: rideLocationData?.state?.value,
          country: rideLocationData.country,
          city: rideLocationData.city,
          zipCode: rideLocationData.zipCode,
        }
      : newData;

    if (supervisorPermissions.canDocument && supervisorPermissions.canCreate) {
      try {
        const res = await createOrUpdateLocation({
          body: { ...dynamicData, country: dynamicData.country.value },
          type: 'create',
        }).unwrap();

        if (shouldSupervisorApprove) {
          const { address1, address2, state, city, zipCode } = dynamicData;

          const noteDetail = `${isEmptyStr(address1)}, ${isEmptyStr(address2)} ${isEmptyStr(city)} ${isEmptyStr(toStateAbbreviation(state))} ${isEmptyStr(zipCode)} | ${isEmptyStr(info?.firstName || '')} ${isEmptyStr(info?.lastName || '')} | ${moment().format('MM/DD/YYYY HH:mm z')}`;
          await createOrUpdateNotes({
            data: {
              noteDetail,
              // TODO do we need these associated id here anymore???
              // associatedId: info?.id,
              // associatedId: userInfo.id,
              category: DEFAULT_NOTE_CATEGORY.supervisor.id,
              isLocationVerified: false,
            },
            type: currentPath === 'home' ? 'member' : currentPath,
            supervisorId: info?.id,
          }).unwrap();
        }

        change?.({ target: { name: 'location', value: res } });
        toast.success(toastMessage.COMMON.locationCreatedSuccessfully);
        return { hasError: false, message: 'Success' };
      } catch (err) {
        toastError(err, toastMessage.COMMON.failedToCreateLocation);
        return { hasError: true, message: 'Failed' };
      } finally {
        setValueHasSaved(true);
        dispatch(setSupervisorApprove(false));
        setToggleLocationModal({
          toggle: false,
          address: null,
        });
      }
    } else {
      toast.error(toastMessage.COMMON.noPermissionToCreateLocation);
      return { hasError: true, message: 'Failed' };
    }
  };

  const handleAuthSuccess = useCallback(
    async (info) => {
      await handleSaveDataAfterNote(getValues(), info);
    },
    [getValues, handleSaveDataAfterNote]
  );

  return (
    <FormProvider {...methods}>
      {children}
      {toggleLocationModal.toggle && !isInModal && (
        <LocationModal
          closeModal={() => {
            setToggleLocationModal({
              toggle: false,
              address: null,
            });
            dispatch(setSupervisorApprove(false));
          }}
          override={
            toggleLocationModal?.address ? toggleLocationModal?.address : false
          }
          setValue={setSavedValue}
          change={change}
          success={handleAuthTrigger}
          error={() => setValueHasSaved(false)}
          index={index}
          setVerifiedAddress={setVerifiedAddress}
          searchInputValue={searchInputValue}
          dataTestId={dataTestId}
        />
      )}
      {openAuthModal && !toggleLocationModal?.address && (
        <AuthModal
          dataTestId={`${dataTestId}-auth-modal`}
          closeClick={handleCloseAuthModal}
          toggleSupervisorOverride={setToggleSupervisorOverride}
          success={(info) => handleAuthSuccess(info)}
        />
      )}
      {/* TODO: remove after testing */}
      {/* {toggleSupervisorOverride && (
        <NotesModal
          dataTestId={`${dataTestId}-note-modal`}
          closeModal={() => {
            setToggleSupervisorOverride(false);
            dispatch(setSupervisorApprove(true));
          }}
          success={async () => await handleSaveDataAfterNote(getValues())}
          override={true}
          isSupervisor
          associatedId={userInfo.id}
          defaultSelect={DEFAULT_NOTE_CATEGORY.supervisor}
        />
      )} */}
    </FormProvider>
  );
};

export default memo(LocationModalWrapper);
